import { TrendingDown, TrendingUp, Users, UsersIcon, Loader2 } from 'lucide-react';
import React from 'react';

import { useGetDashboardStats } from '../../../services/admin.stats.service';

const MetricsCard = ({ icon, title, value, subtitle, trendValue }) => {
  const isPositive = trendValue >= 0;
  const trendText = `${isPositive ? '+' : ''}${trendValue}% since last month`;
  
  return (
    <div className="bg-white dark:bg-[#1d2026] rounded-[20px] p-6 border border-gray-100 dark:border-[#32353c] shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-gray-50 dark:bg-[#0b0e14] p-2.5 rounded-xl text-gray-600 dark:text-gray-300 transition-colors">
            {icon}
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">{title}</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {value}
              </p>
            </div>
          </div>
        </div>
        <div className="text-right">
          <span className="text-sm text-gray-400 dark:text-gray-500">{subtitle}</span>
        </div>
      </div>
      {trendValue !== undefined && (
        <p className={`text-[10px] font-normal mt-3 flex items-center gap-1 ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
          <span className="inline-block">
         {isPositive ?
             <TrendingUp size={10}/>
             : <TrendingDown size={10}/>
         }
          </span>
          {trendText}
        </p>
      )}
    </div>
  );
};

const GridIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const MetricsDashboard = () => {
  const { data: stats, isLoading, isError, isFetching } = useGetDashboardStats();

  if (isError) return null;

  const showLoader = isLoading || isFetching;

  return (
    <div className="w-full relative">
      <div className="max-w-7xl mx-auto">
        {/* Central Loader Overlay */}
        {showLoader && (
          <div className="absolute top-0 right-0 p-4 z-10">
              <div className="flex items-center gap-2 bg-white/80 dark:bg-[#1d2026]/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-100 dark:border-[#32353c] shadow-sm">
                <Loader2 className="w-4 h-4 text-[#174568] dark:text-blue-400 animate-spin" />
                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Updating</span>
              </div>
          </div>
        )}

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity duration-300 ${isLoading && !stats ? 'opacity-50' : 'opacity-100'}`}>
          <MetricsCard
            icon={<GridIcon />}
            title="Active projects"
            value={stats?.activeProjects?.value ?? "0"}
            trendValue={stats?.activeProjects?.trend}
          />
          
          <MetricsCard
            icon={<GridIcon />}
            title="Completed"
            value={stats?.completedProjects?.value ?? "0"}
            trendValue={stats?.completedProjects?.trend}
          />
          
          <MetricsCard
            icon={<Users className="w-6 h-6" />}
            title="Total Hellocian"
            value={stats?.hellocians?.value ?? "0"}
            trendValue={stats?.hellocians?.trend}
          />
          
          <MetricsCard
            icon={<UsersIcon className="w-6 h-6" />}
            title="Clients Registered"
            value={stats?.clients?.value ?? "0"}
            trendValue={stats?.clients?.trend}
          />
        </div>
      </div>
    </div>
  );
};

export default MetricsDashboard;